import { Phone, Home, MapPin, Clock, CheckCircle, User, Calendar, Package } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import './HomeVisits.css';

const HomeVisits = () => {
  const whatsappUrl = 'https://wa.me/919886332528';

  const whoNeeds = [
    { icon: <User size={32} />, text: 'Post-surgery patients' },
    { icon: <User size={32} />, text: 'Elderly patients' },
    { icon: <User size={32} />, text: 'Stroke/paralysis patients' },
    { icon: <User size={32} />, text: 'Limited mobility' },
    { icon: <User size={32} />, text: 'Fracture recovery' },
    { icon: <User size={32} />, text: 'Busy working adults' },
    { icon: <User size={32} />, text: 'Children' },
    { icon: <User size={32} />, text: 'Chronic pain patients' }
  ];

  const areas = [
    'Panjim', 'Miramar', 'Caranzalem', 'Dona Paula', 'Porvorim', 
    'Saligao', 'Mapusa', 'Candolim', 'Calangute', 'Baga',
    'Anjuna', 'Vagator', 'Ponda', 'Margao', 'Vasco da Gama'
  ];

  const whyChoose = [
    {
      icon: <CheckCircle size={24} />,
      title: 'BPT-Qualified Certified Physiotherapists',
      description: 'All our home visit physiotherapists are certified professionals with years of experience.'
    },
    {
      icon: <Package size={24} />,
      title: 'Portable Professional Equipment',
      description: 'We bring TENS, IFT, and other necessary equipment to your home for effective treatment.'
    },
    {
      icon: <User size={24} />,
      title: 'Same Therapist for Every Visit',
      description: 'Build rapport and continuity with the same physiotherapist throughout your treatment.'
    },
    {
      icon: <Phone size={24} />,
      title: 'Easy WhatsApp Communication',
      description: 'Book, reschedule, and communicate easily via WhatsApp for your convenience.'
    }
  ];

  return (
    <div className="home-visits-page">
      {/* Hero Section */}
      <section className="hv-hero">
        <div className="container">
          <div className="hv-hero-content">
            <h1>Physiotherapy Home Visits in Panjim & Across Goa</h1>
            <p className="hv-subtitle">
              Certified physiotherapists come to your home — at a time that suits you
            </p>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn-primary large">
              <Phone size={20} />
              Request a Home Visit — WhatsApp Now
            </a>
          </div>
        </div>
      </section>

      {/* Who Needs Home Visits */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Who Needs Home Visits?</h2>
            <p>Home physiotherapy is ideal for those who cannot easily visit a clinic</p>
          </div>
          <div className="who-needs-grid">
            {whoNeeds.map((item, index) => (
              <div key={index} className="who-needs-card">
                <div className="who-needs-icon">{item.icon}</div>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section how-it-works-section">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p>Simple 3-step process to get expert physiotherapy at home</p>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <Phone size={40} className="step-icon" />
              <h3>WhatsApp Us</h3>
              <p>Send us your location and condition via WhatsApp</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <Calendar size={40} className="step-icon" />
              <h3>Confirm Schedule</h3>
              <p>We confirm a certified physio and your preferred time</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <Home size={40} className="step-icon" />
              <h3>Physio Arrives</h3>
              <p>Your physiotherapist arrives at home with all portable equipment</p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas We Cover */}
      <section className="section areas-section">
        <div className="container">
          <div className="section-header">
            <h2>Areas We Cover</h2>
            <p>Home visit physiotherapy services across all major areas in Goa</p>
          </div>
          <div className="areas-grid">{areas.map((area, index) => (
              <div key={index} className="area-tag">
                <MapPin size={16} />
                <span>{area}</span>
              </div>
            ))}
          </div>
          <div className="section-cta">
            <p className="areas-note">Plus all surrounding areas across North and South Goa</p>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section why-home-visits">
        <div className="container">
          <div className="section-header">
            <h2>Why Choose Dr. Manaz for Home Visits?</h2>
          </div>
          <div className="why-home-grid">
            {whyChoose.map((item, index) => (
              <Card key={index} className="why-home-card">
                <CardContent className="why-home-content">
                  <div className="why-home-icon">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section faq-section">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="faq-container">
            <Accordion type="single" collapsible className="faq-accordion">
              <AccordionItem value="item-1">
                <AccordionTrigger>What equipment does the physio bring to a home visit?</AccordionTrigger>
                <AccordionContent>
                  Our physiotherapists bring portable equipment including TENS/IFT machines for electrical stimulation, ultrasound therapy devices, exercise bands, therapeutic balls, and other necessary tools based on your condition. We ensure you receive the same quality of treatment as you would at our clinic.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger>How is the home visit fee structured?</AccordionTrigger>
                <AccordionContent>
                  Home visit fees depend on your location and the type of treatment required. Contact us on WhatsApp for a detailed quote based on your specific needs. We offer competitive pricing and package deals for multiple sessions.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger>Can a home visit physio handle post-surgery rehabilitation?</AccordionTrigger>
                <AccordionContent>
                  Absolutely! Our certified physiotherapists are fully trained in post-surgical rehabilitation. We handle all types of post-operative care including orthopedic surgery, joint replacement, cardiac surgery recovery, and more. We'll work with your surgeon's protocols to ensure safe and effective recovery.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger>How quickly can I get a home visit in Panjim?</AccordionTrigger>
                <AccordionContent>
                  For Panjim and surrounding areas like Miramar, we can often arrange a home visit within 24 hours. For other parts of Goa, scheduling depends on availability but we strive to accommodate urgent cases as quickly as possible. Contact us on WhatsApp for immediate scheduling.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger>Do you provide home visits on weekends?</AccordionTrigger>
                <AccordionContent>
                  Yes, we provide home visit services on Saturdays. For Sunday appointments, please contact us in advance as availability may be limited. We understand that many working professionals need weekend appointments and we do our best to accommodate.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="section-dark cta-footer">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Experience Expert Physiotherapy at Home?</h2>
            <p>Book your home visit now and take the first step towards recovery</p>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="btn-primary large">
              <Phone size={20} />
              Book Your Home Visit Now — WhatsApp 9886332528
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeVisits;